<template>
    <ValidationObserver
        ref="testCalculation"
        v-slot="{ passes}"
    >
        <BaseDialog
            :showDialog="showDialog"
            headline="lang_PUBLISHING_WORKS_ACTION_OPTION_TEST_CALCULATION_GEMA"
            :close="closeDialog"
            maxDialogWidth="1500"
            maxCardHeight="800"
            :alvqa="alvqa"
        >
            <template v-slot:dialogText>
                <v-container fluid>
                    <v-row class="pl-3 headline">
                        {{ getWorkName() }}
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Amount"
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_AMOUNT')"
                                rules="required|amount"
                                :withMargin="false"
                                :alvqa="alvqa + '-textfield-amount'"
                                :suffix="$store.getters.getSelectedCurrency"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="context"
                                field="Rights"
                                :items="rightsItems" 
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_TYPE_OF_RIGHTS')"
                                :selection="true"
                                rules="required"
                                :withMargin="false"
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-rights'"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="context"
                                field="Country"
                                :items="countryItems" 
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_ORIGINATING_COUNTRY')"
                                :selection="true"
                                :withMargin="false"
                                :alvqa="alvqa + '-dropdown-country'"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageAutocomplete 
                                :context="context"
                                field="Ykto"
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_YKTO')"
                                :withMargin="false"
                                rules="required"
                                :additionalFunction="fillInKtos"
                                :alvqa="alvqa + '-autocomplete-ykto-textfield'"
                            >
                                <template v-slot:selection="{ item }">
                                    <div class="ellipsis" :alvqa="alvqa + '-autocomplete-ykto-selection'">
                                        {{ 
                                            typeof item === 'object' ? 
                                                item.ykto : item 
                                        }}
                                    </div>
                                </template>
                                <template v-slot:items="{ items }">
                                    <v-list-item-content :alvqa="alvqa + '-autocomplete-ykto-dropdown-' + items.alvId">
                                        <v-list-item-title :alvqa="alvqa + '-autocomplete-ykto-dropdown-' + items.alvId + '-title'">
                                            {{ items.gemaName }} ({{ items.alvId }}) [KTO: {{ items.metadata.ykto }} - {{ items.metadata.hkto }} - {{ items.metadata.ukto }}]
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </BaseEditPageAutocomplete>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="context"
                                field="Bg"
                                :items="bgItems" 
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_BG')"
                                :withMargin="false"
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-bg'"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Hkto"
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_HKTO')"
                                :withMargin="false"
                                rules="required"
                                :alvqa="alvqa + '-textfield-hkto'"
                            /> 
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="GdfPercent"
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_GDF_PERCENT')"
                                suffix="%"
                                rules="percentDecimal|required"
                                :withMargin="false"
                                :alvqa="alvqa + '-textfield-gdf-percent'"
                            /> 
                        </v-col>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Ukto"
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_UKTO')"
                                :withMargin="false"
                                rules="required"
                                :alvqa="alvqa + '-textfield-ukto'"
                            /> 
                        </v-col>
                    </v-row>
                    <v-divider />
                    <v-row v-if="errorMessage !== null" class="pt-3">
                        <v-col>
                            <v-chip  
                                small 
                                label
                                color="errorLineChip"
                                text-color="errorLineText"
                                class="mr-6"
                                :alvqa="alvqa + 'result-chip-error'"
                            >{{ $t('lang_GENERAL_CAPTION_ERROR') }}</v-chip>
                            {{  $t(errorMessage) }}
                        </v-col>
                    </v-row>
                    <v-row v-for="(entry, sparte) in result" :key="sparte" class="pt-3">
                        <v-col>
                            <v-chip 
                                label 
                                color="resultHeadlineBackground" 
                                text-color="resultHeadlineText"
                                :alvqa="alvqa + '-result-list-chip-type-of-right-' + sparte.toLowerCase().replace(/\s+/g, '-')"
                            >{{ sparte }}</v-chip>
                            <v-row>
                                <v-col>
                                    <v-data-table
                                        v-if="entry[0].error === -1"
                                        :headers="resultHeaders"
                                        :items="entry"
                                        hide-default-footer
                                        disable-sort
                                        dense
                                        :items-per-page="itemsPerPage"
                                    >
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td>
                                                    <v-chip  
                                                        small 
                                                        label
                                                        color="excelLineChip"
                                                        text-color="excelLineText"
                                                        :alvqa="alvqa + 'result-list-row-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-chip-excel'"
                                                    >
                                                        <span class="mr-2 ml-1">{{ $t('lang_GENERAL_CAPTION_EXCEL') }}</span>
                                                    </v-chip>
                                                </td> 
                                                <td 
                                                    class="ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-statement-for'"
                                                ><BaseTooltip :simpleText="item.name_alv" /></td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-amount'"
                                                > {{ $alvPlugin.getNumberLocalization(item.total_net_val, 'number') }}</td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-base-percent'"
                                                > {{ $alvPlugin.getNumberLocalization(item.contract_base, 'number') }}
                                                </td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-share-percent'"
                                                > {{ $alvPlugin.getNumberLocalization(item.contract_share, 'number') }}
                                                </td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-share-amount'"
                                                > {{ $alvPlugin.getNumberLocalization(item.eur_net, 'number') }}
                                                </td>
                                                <td 
                                                    class="ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-calculation-rule'"
                                                ><BaseTooltip :simpleText="item.rule_applied" /></td>
                                                <td 
                                                    class="no_ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-publishing-contract'"
                                                > {{ item.contract_match_id }}</td>
                                                <td 
                                                    class="no_ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-contract-type'"
                                                > {{ item.type_of_contract }}</td>
                                            </tr>
                                            <tr v-if="item.rowInfo !== undefined" class="pt-3">
                                                <td></td>
                                                <td colspan="8" :alvqa="alvqa + 'result-list-row-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-contract-line'">
                                                        <v-chip  
                                                            small 
                                                            label
                                                            color="contractLineChip"
                                                            class="mr-6"
                                                            :alvqa="alvqa + 'result-list-row-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-chip-contract'"
                                                        >{{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_CONTRACT_ROW') }}</v-chip>
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_RIGHT_TYPE') }}: {{ item.rowInfo.rights !== '' ? $t(getContractRightConst(item.rowInfo.rights)) : '--' }} | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_SHARE') }}: {{ item.rowInfo.percent !== '' ? $n(item.rowInfo.percent, 'number') : '--' }} % | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_BASE') }}: {{ item.rowInfo.base !== '' ? $n(item.rowInfo.base, 'number') : '--' }} % | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_REGION') }}: {{ item.rowInfo.region !== '' ? item.rowInfo.region : '--' }} | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_APPLY_TO') }}: {{ item.rowInfo.refundFrom !== '' ? getApplyTo(item.rowInfo.refundFrom) : '--' }}
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                    <div v-else-if="entry[0].error === 1" class="pt-3">
                                        <v-chip  
                                            small 
                                            label
                                            color="errorLineChip"
                                            text-color="errorLineText"
                                            class="mr-6"
                                            :alvqa="alvqa + '-result-chip-error'"
                                        >{{ $t('lang_GENERAL_CAPTION_ERROR') }}</v-chip>
                                        <span :alvqa="alvqa + '-result-error-text'">
                                            {{ entry[0].rowInfo.error }}
                                        </span>
                                    </div>
                                </v-col> 
                            </v-row>
                        </v-col>
                    </v-row>
                </v-container>
            </template>
            <template v-slot:dialogActions>
                <v-container fluid>
                    <v-row>
                        <v-spacer />
                        <v-col class="text-right">
                            <BaseButton 
                                label="lang_GENERAL_CAPTION_CALCULATE"
                                @click="passes(calculate)"
                                :alvqa="alvqa+'-button-calculate'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </BaseDialog>
    </ValidationObserver>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageAutocomplete from '@/components/_editForm/BaseEditPageAutocomplete'
import BaseTooltip from '@/components/_base/BaseTooltip'

import { DropdownContractsPublishingApplyTo } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "TestCalculationGemaDialog",
    components: {
        BaseDialog,
        BaseButton,
        BaseEditPageTextField,
        BaseEditPageDropdown,
        BaseEditPageAutocomplete,
        BaseTooltip
    },
    data() {
        return {
            context: "TestCalculationGema",
            alvqa: 'publishing-works-list-dialog-test-calculation-gema'
        }
    },
    computed: {
        itemsPerPage() { return this.$store.getters.getItemsPerPage },

        // Dropdowns
        rightsItems() { 
            let rightsArr
            let rights = this.$store.getters.getTestCalculationGemaFormTypeOfRightsValues 

            if(rights !== undefined) {
                rightsArr = JSON.parse(JSON.stringify(rights))
                rightsArr.forEach(element => {
                    element['alvqa'] = this.alvqa + '-dropdown-rights-' + element.value
                });
            }

            return rightsArr
        },
        bgItems() { 
            let bgArr
            let bg = this.$store.getters.getTestCalculationGemaFormBgValues 

            if(bg !== undefined) {
                bgArr = JSON.parse(JSON.stringify(bg))
                bgArr.forEach(element => {
                    element['alvqa'] = this.alvqa + '-dropdown-bg-' + element.value
                });
            }

            return bgArr
        },

        countryItems() { return this.$alvPlugin.getListOfTranslatedCountries() },

        result: {
            get() { return this.$store.getters.getTestCalculationGemaResult},
            set(result) { this.$store.commit('setTestCalculationGemaResult', result) }
        } ,
        errorMessage: { 
            get() { return this.$store.getters.getTestCalculationGemaResultErrorMessage },
            set(msg) { this.$store.commit('setTestCalculationGemaResultErrorMessage', msg) }
        },

        showDialog: {
            get() { return this.$store.getters.getTestCalculationGemaDialog },
            set(show) { this.$store.commit('setTestCalculationGemaDialog', show) }
        },

        resultHeaders() {
            return [
                {
					text: '', 
					value: 'contractLine', 
                    align: 'center',
                    width: "95px",
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_STATEMENT_FOR'), 
					value: 'name_alv', 
					align: 'left',
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_AMOUNT') + ' ' + this.$store.getters.getSelectedCurrency,
					value: 'total_net_val', 
                    align: 'right', 
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                }, 
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_BASE') + ' %',
					value: 'contract_base', 
                    align: 'right', 
                    width: "90px",
                    sortable: false,
					class: 'text-no-wrap'
                }, 
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_SHARE') + ' %',
					value: 'contract_share', 
                    align: 'right', 
                    width: "90px",
                    sortable: false,
					class: 'text-no-wrap'
                }, 
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_SHARE') + ' ' + this.$store.getters.getSelectedCurrency,
					value: 'eur_net', 
                    align: 'right', 
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_CALCULATION_RULE'), 
					value: 'rule_applied', 
					align: 'left',
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_PUBLISHING_CONTRACT'), 
					value: 'contract_match_id', 
                    align: 'left',
                    width: "160px",
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_GENERIC_CONTRACTS_CAPTION_CONTRACT_TYPE'), 
					value: 'type_of_contract', 
                    align: 'left',
                    width: "125px",
                    sortable: false,
					class: 'text-no-wrap'
                }
            ]
        }
    },
    methods: {
        closeDialog() {
            this.showDialog = false
            this.$store.commit('setSelectedWorksAction', [])
            this.$store.commit('setSelectedWorks', [])
            this.result = []
            this.errorMessage = null
            this.$store.commit('setTestCalculationGemaYktoOptions', [])
            this.$store.commit('setIsAllSelectedWorks', false)
        },
        calculate() {
            this.result = []
            this.errorMessage = null
            this.$store.dispatch('calculateGemaTestCalculation')
        },
        getContractRightConst(right) {
            let contractRights = this.$store.getters.getContractsRights
            let text = ''
            contractRights.forEach(entry => {
                if(entry.value === right) text = entry.text
            });

            return text
        },
        getApplyTo(value) {
            let values = DropdownContractsPublishingApplyTo
            
            let match = values.find(entry => entry.value === value)
            if(match === undefined) return ''
            return this.$t(match.text)
        },
        fillInKtos(item) {
            if(item !== null && item !== undefined) {
                this.$store.commit('setTestCalculationGemaDetailsHkto', item.hkto)
                this.$store.commit('setTestCalculationGemaDetailsUkto', item.ukto)
            }            
        },
        getWorkName() {
            let selectedWorkAlvId = this.$store.getters.getSelectedWorks
            let works = this.$store.getters.getWorks
            let title = ''

            let match = works.find(work => work.alvId === selectedWorkAlvId[0])
            if(match !== undefined) {
                title += match.title + ' (' + match.alvId
                if(match.gemaNumber !== null && match.gemaNumber !== '') title += ' | ' + match.gemaNumber
                title += ')'
            }
            return title
        }
    },
    created() {
        this.$store.commit('setCountries')
    }
}
</script>